import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Stack, Typography, Button } from '@mui/material';
import { ConfirmationNumber as ConfirmationNumberIcon } from '@mui/icons-material';
import styles from '../../../../assets/css/components/MobileLobby.module.css';
import freerollIcon from '/images/lobby/freeroll.svg';
import satelliteIcon from '/images/lobby/satellite.svg';
import chipsIcon from '/images/lobby/mobile/chip.svg';
import playersIcon from '/images/lobby/mobile/players.svg';
import crownIcon from '/images/lobby/mobile/crown.svg';
import { IUserState } from '../../../../Core/State/IUserState';
import { ITournamentState } from '../../../../Core/State/ILobbyTournamentState';
import { InteractionHandler } from '../../../../Core/Handler/InteractionHandler';
import { t } from '../../../../i18n';
import {
    formatCurrency,
    isLateRegistrationOpen,
    getEffectivePrize,
    getEligibleTickets,
    getStateColor,
    getStartText,
    formatNumber,
    canRegisterForTournament,
    getTournamentRemainingPlayers,
    getTournamentTotalEntrants,
    isTournamentRegisteredByUser,
    shouldShowTournamentRemainingCount
} from '../../../../Utils/TournamentUtils';

interface MobileTournamentTableRowProps {
    tournament: ITournamentState;
    userState: IUserState;
    handleClick: (gameId: string) => void;
    interactionHandler?: InteractionHandler;
}

import defaultTourneyImg from '/images/tournament/default.png';

const getPlayerCount = (tournament: ITournamentState) => {
    const totalEntrants = getTournamentTotalEntrants(tournament);
    if (!shouldShowTournamentRemainingCount(tournament)) return totalEntrants;
    return `${getTournamentRemainingPlayers(tournament)} / ${totalEntrants}`;
};

const MobileTournamentTableRow: React.FC<MobileTournamentTableRowProps> = ({
    tournament,
    userState,
    handleClick,
    interactionHandler
}) => {
    const [actionCooldown, setActionCooldown] = useState(false);
    const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isUserRegistered = isTournamentRegisteredByUser(tournament, userState.id);
    const canRegisterNow = canRegisterForTournament(tournament, new Date());
    const hasSpace = tournament.registeredPlayerCount < tournament.maxPlayers;
    const eligibleTickets = getEligibleTickets(userState.tickets || [], tournament);
    const canUnregister = isUserRegistered && new Date() < new Date(tournament.startTime);
    const entryMode = tournament.registrationEntryMode || 'cash_only';
    const cashAllowed = entryMode === 'cash_only' || entryMode === 'cash_or_ticket';
    const ticketAllowed = entryMode === 'ticket_only' || entryMode === 'cash_or_ticket';
    const hasEligibleTicket = eligibleTickets.length > 0;
    const showTicketButton = ticketAllowed && hasEligibleTicket;
    const showTicketDisabled = ticketAllowed && !hasEligibleTicket && entryMode === 'ticket_only';

    const runWithCooldown = useCallback((action: () => void) => {
        if (!interactionHandler || actionCooldown) return;

        setActionCooldown(true);
        action();
        cooldownTimerRef.current = setTimeout(() => {
            setActionCooldown(false);
            cooldownTimerRef.current = null;
        }, 2000);
    }, [actionCooldown, interactionHandler]);

    useEffect(() => {
        return () => {
            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current);
            }
        };
    }, []);

    return (
    <Stack key={tournament.gameId} direction="row" spacing={0.5} className={styles.mobileTable} sx={{ '--game-type-color': '#19E5E0', '--state-color': (isLateRegistrationOpen(tournament) ? '#F93751' : getStateColor(tournament.status)) }} onClick={() => handleClick(tournament.gameId)}>
        <Box className={styles.mobileTablePlayers} style={{ padding: '5px 8px 5px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Stack alignItems="center">
                <Box className={styles.tourneyLogo}>
                    <img
                        src={tournament.imageName ? `${import.meta.env.BASE_URL}images/tournament/${tournament.imageName}` : defaultTourneyImg}
                        alt="Tournament logo"
                        onError={(e) => {
                            e.currentTarget.src = defaultTourneyImg;
                        }}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                </Box>
            </Stack>
        </Box>
        <Stack className={styles.mobileMttTableInfo}>
            <Stack direction="row" className={styles.mobileMttTableTop}>
                <Stack style={{ width: '100%' }}>
                    <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                        <Typography className={styles.tourneyStartTime} style={{ marginRight: 'auto'}}>{ getStartText(tournament) }</Typography>
                        { tournament.status === 'registration' && isUserRegistered ? (
                            <Typography className={styles.mobileTableRegistered} style={{ marginLeft: 8, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                                <span>{t('Tournament:box.registered')}</span>
                            </Typography>
                        ) : (
                            <Typography className={styles.mobileTableState}>{(() => {
                                if (isLateRegistrationOpen(tournament)) return t('Tournament:status.lateRegistration');
                                if (tournament.status === 'running') return t('Tournament:status.inPlay');
                                const statusMap: Record<string, string> = {
                                    'registration': t('Tournament:status.registration'),
                                    'late_registration': t('Tournament:status.lateRegistration'),
                                    'scheduled': t('Tournament:status.registration'),
                                    'completed': t('Tournament:status.completed'),
                                    'cancelled': t('Tournament:status.cancelled'),
                                    'paused': t('Tournament:status.onBreak'),
                                };
                                return statusMap[tournament.status] || tournament.status;
                            })()}</Typography>
                        )}
                        {tournament.isSatellite && (
                            <img src={satelliteIcon} alt="Satellite Icon" style={{ marginLeft: 8 }} height={14} />
                        )}
                        {tournament.buyIn === 0 && (
                            <img src={freerollIcon} alt="Freeroll Icon" style={{ marginLeft: 8 }} height={14} />
                        )}
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" className={styles.mobileMttTableTitle}>
                        <Typography className={styles.mobileTourneyName}>{tournament.templateName || tournament.name}</Typography>
                    </Stack>
                </Stack>
            </Stack>
            <Stack direction="row" className={styles.mobileMttTableStakes}>
                <Typography alignItems="center">
                    <img src={chipsIcon} alt="Chip Icon" style={{ marginRight: '4px' }} /> {tournament.buyIn == 0 ? t('Tournament:gameTypes.freeroll') : `$${formatNumber(tournament.buyIn)} + $${formatNumber(tournament.entryFee)}`}
                    <span style={{ marginLeft: "16px" }}></span>
                    <img src={playersIcon} alt="Players Icon" style={{ marginRight: '4px' }} /> {getPlayerCount(tournament)}
                    <span style={{ marginLeft: "16px" }}></span>
                    <img src={crownIcon} alt="Crown Icon" height="14" style={{ marginRight: '4px' }} />
                    {formatCurrency(getEffectivePrize(tournament))}
                </Typography>
            </Stack>
            {canUnregister && interactionHandler && (
                <Stack direction="row" sx={{ mt: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={actionCooldown}
                        sx={{ flex: 1, fontWeight: 700 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            runWithCooldown(() => interactionHandler.unsignFromTournament(tournament.gameId));
                        }}
                    >
                        {t('Tournament:actions.unregister')}
                    </Button>
                </Stack>
            )}
            {!isUserRegistered && canRegisterNow && interactionHandler && (
                <Stack direction="row" gap={1} sx={{ mt: 1 }}>
                    {hasSpace ? (
                        <>
                            {cashAllowed && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    disabled={actionCooldown}
                                    sx={{ flex: 1, fontWeight: 700 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        runWithCooldown(() => interactionHandler.signUpForTournament(tournament.gameId));
                                    }}
                                >
                                    {t('Tournament:actions.register')}
                                </Button>
                            )}
                            {showTicketButton && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    disabled={actionCooldown}
                                    sx={{ flex: 1, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        runWithCooldown(() => interactionHandler.registerForTournamentWithTicket(tournament.gameId, eligibleTickets[0]._id));
                                    }}
                                >
                                    <ConfirmationNumberIcon fontSize="small" />{t('Tournament:labels.ticket')}
                                </Button>
                            )}
                            {showTicketDisabled && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    disabled
                                    sx={{ flex: 1, fontWeight: 700 }}
                                >
                                    {t('Tournament:labels.ticketRequired')}
                                </Button>
                            )}
                        </>
                    ) : (
                        <Button variant="contained" color="secondary" disabled size="small" fullWidth sx={{ fontWeight: 700 }}>{t('Tournament:labels.tournamentFull')}</Button>
                    )}
                </Stack>
            )}
        </Stack>
    </Stack>
    );
};

export default MobileTournamentTableRow;
